/**
 * RollOver provides image rollover effects for navigation elements.
 * Supports multiple image states and event registration.
 */

// Declare external dependencies

// Module state
const rollovers: Record<string, RollOver> = {};

// Use a getter to always read from globalThis for rollImagePath
// This allows Java-generated scripts to set rollImagePath before calling createRollover
function getRollImagePath(): string {
  return (globalThis as any).rollImagePath || "";
}

/**
 * RollOver class for managing image rollover states
 */
class RollOver {
  imgName: string;
  enabled: boolean;
  images: HTMLImageElement[];

  constructor(name: string) {
    this.imgName = name;
    this.enabled = true;
    this.images = [];
  }

  /**
   * Changes the image to the specified state
   * @param which - Image index or image source URL
   */
  roll(which: number | string): this {
    if (this.enabled) {
      const elements = document.getElementsByName(this.imgName);
      if (elements.length > 0) {
        (elements[0] as HTMLImageElement).src =
          typeof which === "number" ? this.images[which].src : which;
      }
    }
    return this;
  }

  /**
   * Adds an image to the rollover states
   * @param imageSrc - Image source URL
   */
  addImage(imageSrc: string): void {
    const i = this.images.length;
    this.images[i] = new Image();
    this.images[i].src = imageSrc;
  }

  /**
   * Disables rollover effects
   */
  disable(): this {
    this.enabled = false;
    return this;
  }

  /**
   * Enables rollover effects
   */
  enable(): this {
    this.enabled = true;
    return this;
  }
}

/**
 * Creates a new rollover with specified images
 * @param name - Element name for the rollover
 * @param args - Image paths for rollover states
 */
function createRollover(name: string, ...args: string[]): RollOver {
  const newRoll = new RollOver(name);
  rollovers[name] = newRoll;

  const imagePath = getRollImagePath();
  for (const arg of args) {
    newRoll.addImage(imagePath + arg);
  }

  return newRoll;
}

/**
 * Triggers rollover to specified state
 * @param name - Rollover name
 * @param which - State index or image URL
 */
function roll(name: string, which: number | string): void {
  rollovers[name].roll(which);
}

/**
 * Triggers rollover to "on" state (index 1)
 */
function on(name: string): void {
  rollovers[name].roll(1);
}

/**
 * Triggers rollover to "off" state (index 0)
 */
function off(name: string): void {
  rollovers[name].roll(0);
}

/**
 * Creates a rollover with automatic mouse event registration
 * @param name - Element name
 * @param img - Normal image path
 * @param imgHi - Hover image path
 */
function createAutoRollover(name: string, img: string, imgHi: string): void {
  createRollover(name, img, imgHi);
  registerEvent("mouseover", name, function() { on(name); });
  registerEvent("mouseout", name, function() { off(name); });
}

/**
 * Sets the base path for rollover images
 */
function setRollImagePath(path: string): void {
  (globalThis as any).rollImagePath = path;
}

/**
 * Gets a rollover by name
 */
function getRollover(name: string): RollOver | undefined {
  return rollovers[name];
}

// Export for module usage
export {
  RollOver,
  createRollover,
  roll,
  on,
  off,
  createAutoRollover,
  setRollImagePath,
  getRollover,
  rollovers,
  getRollImagePath
};

// Expose on globalThis for backwards compatibility
(globalThis as any).RollOver = RollOver;
(globalThis as any).rollovers = rollovers;
// Initialize rollImagePath on globalThis - Java code sets this before calling createRollover
(globalThis as any).rollImagePath = "";
(globalThis as any).createRollover = createRollover;
(globalThis as any).roll = roll;
(globalThis as any).on = on;
(globalThis as any).off = off;
(globalThis as any).createAutoRollover = createAutoRollover;

// Script resource notification
if (typeof _SR_ !== "undefined" && _SR_ !== null) {
  _SR_.notify("rollover.js");
}
